import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./AnswerTenPointSlider.css";
import stronglyAgree from "../../assets/strongly_agree.gif";
import agree from "../../assets/agree.gif";
import neutral from "../../assets/neutral.gif";
import sad from "../../assets/sad.gif";
import stronglySad from "../../assets/strongly_sad.gif";

const mapValuesOfEmojies = {
  1: stronglySad,
  2: sad,
  3: neutral,
  4: agree,
  5: stronglyAgree,
};

function toIntAnswerCode(answerCode) {
  const n = Number.parseInt(String(answerCode ?? "").trim(), 10);
  return Number.isNaN(n) ? null : n;
}

/** 10-point: 1–6 sad, 7–8 neutral, 9–10 agree. */
export function resolveTenPointThreeTierGif(codeOrValue) {
  const n =
    typeof codeOrValue === "number"
      ? codeOrValue
      : toIntAnswerCode(codeOrValue);
  if (n === null || n < 1) return neutral;
  if (n <= 6) return sad;
  if (n <= 8) return neutral;
  if (n <= 10) return agree;
  return neutral;
}

function TenPointOutlineSad() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.65" />
      <circle cx="8.5" cy="10" r="1" fill="currentColor" />
      <circle cx="15.5" cy="10" r="1" fill="currentColor" />
      <path
        d="M8.5 15.5C10 13.6 14 13.6 15.5 15.5"
        stroke="currentColor"
        strokeWidth="1.65"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

function TenPointOutlineHappy() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.65" />
      <circle cx="8.5" cy="9.5" r="1" fill="currentColor" />
      <circle cx="15.5" cy="9.5" r="1" fill="currentColor" />
      <path
        d="M8.5 14.2C10 16.2 14 16.2 15.5 14.2"
        stroke="currentColor"
        strokeWidth="1.65"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

/** Brief pause after release so emoji/rating is visible before the card closes. */
const COMMIT_DELAY_MS = 450;

/** Demo / info modal: 10 answer options like survey API (10 → 1). */
export function buildTenPointAnswerOptions() {
  return [10, 9, 8, 7, 6, 5, 4, 3, 2, 1].map((code) => ({
    answer: ` ${code}`,
    answer_code: String(code),
    aid: `info-preview-${code}`,
  }));
}

export default function TenPointLikertSlider({
  answer,
  qid,
  gid,
  type,
  onAnswerClick,
  initialAnswerCode,
  onCancel,
  previewOnly = false,
}) {
  const byCode = useMemo(() => {
    const m = new Map();
    answer.forEach((ans) => {
      const c = toIntAnswerCode(ans?.answer_code);
      if (c !== null) m.set(c, ans);
    });
    return m;
  }, [answer]);

  const seedValue = useMemo(() => {
    const fromSaved = toIntAnswerCode(initialAnswerCode);
    if (fromSaved !== null && byCode.has(fromSaved)) return fromSaved;
    if (byCode.has(5)) return 5;
    const keys = [...byCode.keys()].sort((a, b) => a - b);
    return keys.length ? keys[Math.floor(keys.length / 2)] : 5;
  }, [byCode, initialAnswerCode]);

  const [value, setValue] = useState(seedValue);
  const valueRef = useRef(value);
  const commitTimerRef = useRef(null);

  useEffect(() => {
    setValue(seedValue);
  }, [seedValue]);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(
    () => () => {
      if (commitTimerRef.current != null) {
        clearTimeout(commitTimerRef.current);
      }
    },
    []
  );

  const clearPendingCommit = useCallback(() => {
    if (commitTimerRef.current != null) {
      clearTimeout(commitTimerRef.current);
      commitTimerRef.current = null;
    }
  }, []);

  const commit = useCallback(
    (v) => {
      if (previewOnly) return;
      const ans = byCode.get(v);
      if (ans && onAnswerClick) onAnswerClick(ans, qid, gid, type);
    },
    [byCode, onAnswerClick, previewOnly, qid, gid, type]
  );

  const scheduleCommit = useCallback(
    (v) => {
      if (previewOnly) return;
      clearPendingCommit();
      commitTimerRef.current = window.setTimeout(() => {
        commitTimerRef.current = null;
        commit(v);
      }, COMMIT_DELAY_MS);
    },
    [clearPendingCommit, commit, previewOnly]
  );

  const handlePointerDown = useCallback(() => {
    clearPendingCommit();
  }, [clearPendingCommit]);

  const handlePointerUp = useCallback(() => {
    scheduleCommit(valueRef.current);
  }, [scheduleCommit]);

  const handleKeyUp = useCallback(
    (e) => {
      if (previewOnly) return;
      if (
        e.key === "ArrowLeft" ||
        e.key === "ArrowRight" ||
        e.key === "Home" ||
        e.key === "End" ||
        e.key === "PageUp" ||
        e.key === "PageDown"
      ) {
        scheduleCommit(Number(e.currentTarget.value));
      }
    },
    [scheduleCommit, previewOnly]
  );

  const popoverLeftPct = ((value - 1) / 9) * 100;

  return (
    <div
      className="ten-point-likert"
      role="group"
      aria-label={previewOnly ? "10-point rating preview" : "Rating 1 to 10"}
    >
      {!previewOnly && onCancel ? (
        <button
          type="button"
          className="ten-point-likert__close"
          aria-label="Cancel and keep previous rating"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            clearPendingCommit();
            onCancel();
          }}
        >
          ×
        </button>
      ) : null}
      <div className="ten-point-likert__slider-block">
        <div className="ten-point-likert__emoji" aria-hidden="true">
          <img src={resolveTenPointThreeTierGif(value)} alt="" />
        </div>
        <div className="ten-point-likert__track-stack">
          <div className="ten-point-likert__popover-rail" aria-hidden="true">
            <div
              className="ten-point-likert__popover"
              style={{ left: `${popoverLeftPct}%` }}
            >
              <div className="ten-point-likert__popover-inner">
                <span className="ten-point-likert__popover-rating-num">
                  {value}
                </span>
              </div>
            </div>
          </div>
          <div className="ten-point-likert__range-wrap">
            <input
              type="range"
              className="ten-point-likert__range"
              min={1}
              max={10}
              step={1}
              value={value}
              aria-valuemin={1}
              aria-valuemax={10}
              aria-valuenow={value}
              aria-valuetext={`${value} of 10`}
              onChange={(e) => setValue(Number(e.target.value))}
              onPointerDown={handlePointerDown}
              onPointerUp={handlePointerUp}
              onKeyUp={handleKeyUp}
            />
          </div>
          <div className="ten-point-likert__scale-row" aria-hidden="true">
            <span className="ten-point-likert__face-icon">
              <TenPointOutlineSad />
            </span>
            <div className="ten-point-likert__scale-dots">
              {[2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
                <span key={i} className="ten-point-likert__dot" />
              ))}
            </div>
            <span className="ten-point-likert__face-icon">
              <TenPointOutlineHappy />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
